import React from 'react';
import { CustomerData, CustomerDataRow } from '../api';

interface CustomerCardProps {
  customer: CustomerData;
  conversionType: 'INR' | 'Lakhs' | 'Crores';
  onClick?: (customerCode: string, customerName: string) => void;
}

const CustomerCard: React.FC<CustomerCardProps> = ({ customer, conversionType, onClick }) => {
  const formatNumber = (value: number, isPercent = false): string => {
    if (value === 0) return isPercent ? '0.00%' : '0.00';

    let displayValue = value;
    if (!isPercent) {
      switch (conversionType) {
        case 'Lakhs':
          displayValue = value / 100000;
          break;
        case 'Crores':
          displayValue = value / 10000000;
          break;
        case 'INR':
        default:
          displayValue = value;
          break;
      }
    }

    const formatted = displayValue.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return isPercent ? `${formatted}%` : formatted;
  };

  const getRowByCategory = (category: string): CustomerDataRow | undefined => {
    return customer.rows.find(row => row.Category === category);
  };

  const incomeRow = getRowByCategory('INCOME');
  const expensesRow = getRowByCategory('EXPENSES');
  const cmRow = getRowByCategory('CM');
  const cmPctRow = getRowByCategory('CM%');

  const months = [
    { key: 'Apr', label: 'Apr' },
    { key: 'May', label: 'May' },
    { key: 'Jun', label: 'Jun' },
    { key: 'Jul', label: 'Jul' },
    { key: 'Aug', label: 'Aug' },
    { key: 'Sep', label: 'Sep' },
    { key: 'Oct', label: 'Oct' },
    { key: 'Nov', label: 'Nov' },
    { key: 'Dec', label: 'Dec' },
    { key: 'Jan', label: 'Jan' },
    { key: 'Feb', label: 'Feb' },
    { key: 'Mar', label: 'Mar' },
  ];

  return (
    <div 
      className={`w-full overflow-x-auto border border-gray-200 rounded-lg ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
      onClick={() => onClick?.(customer.customer_code, customer.customer_name.trim())}
    >
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-blue-100">
            <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold" colSpan={15}>
              {customer.customer_name.trim()}
            </th>
          </tr>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-2 py-2 text-left text-gray-700 font-bold min-w-[150px]">
              Category
            </th>
            <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
              Total
            </th>
            <th className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
              Average
            </th>
            {months.map(month => (
              <th key={month.key} className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-bold whitespace-nowrap">
                {month.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Income Row */}
          {incomeRow && (
            <tr className="hover:bg-gray-50">
              <td className="border border-gray-200 px-2 py-1.5 text-left font-bold">
                INCOME
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                {formatNumber(incomeRow.Total)}
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                {formatNumber(incomeRow.Average)}
              </td>
              {months.map(month => (
                <td key={month.key} className="border border-gray-200 px-2 py-1.5 text-right">
                  {formatNumber(incomeRow[month.key as keyof CustomerDataRow] as number)}
                </td>
              ))}
            </tr>
          )}

          {/* Expenses Row */}
          {expensesRow && (
            <tr className="hover:bg-gray-50">
              <td className="border border-gray-200 px-2 py-1.5 text-left font-bold">
                EXPENSES
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                {formatNumber(expensesRow.Total)}
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                {formatNumber(expensesRow.Average)}
              </td>
              {months.map(month => (
                <td key={month.key} className="border border-gray-200 px-2 py-1.5 text-right">
                  {formatNumber(expensesRow[month.key as keyof CustomerDataRow] as number)}
                </td>
              ))}
            </tr>
          )}

          {/* CM Row */}
          {cmRow && (
            <tr className="hover:bg-gray-50">
              <td className="border border-gray-200 px-2 py-1.5 text-left font-bold">
                Contribution
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                <span className={cmRow.Total >= 0 ? "text-green-700" : "text-red-700"}>
                  {formatNumber(cmRow.Total)}
                </span>
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                <span className={cmRow.Average >= 0 ? "text-green-700" : "text-red-700"}>
                  {formatNumber(cmRow.Average)}
                </span>
              </td>
              {months.map(month => (
                <td key={month.key} className="border border-gray-200 px-2 py-1.5 text-right">
                  <span className={(cmRow[month.key as keyof CustomerDataRow] as number) >= 0 ? "text-green-700" : "text-red-700"}>
                    {formatNumber(cmRow[month.key as keyof CustomerDataRow] as number)}
                  </span>
                </td>
              ))}
            </tr>
          )}

          {/* CM% Row */}
          {cmPctRow && (
            <tr className="hover:bg-gray-50">
              <td className="border border-gray-200 px-2 py-1.5 text-left font-bold">
                Contribution (%)
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                <span className={cmPctRow.Total >= 0 ? "text-green-700" : "text-red-700"}>
                  {formatNumber(cmPctRow.Total, true)}
                </span>
              </td>
              <td className="border border-gray-200 px-2 py-1.5 text-right font-bold">
                <span className={cmPctRow.Average >= 0 ? "text-green-700" : "text-red-700"}>
                  {formatNumber(cmPctRow.Average, true)}
                </span>
              </td>
              {months.map(month => (
                <td key={month.key} className="border border-gray-200 px-2 py-1.5 text-right">
                  <span className={(cmPctRow[month.key as keyof CustomerDataRow] as number) >= 0 ? "text-green-700" : "text-red-700"}>
                    {formatNumber(cmPctRow[month.key as keyof CustomerDataRow] as number, true)}
                  </span>
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerCard;
